/**
 * features/hr/DeductionReports.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — deduction reports by type (R3).
 *
 * The bursar's remittance view. Pick a month; this reads the
 * GENERATED payslips for that month (the system of record — what was
 * actually deducted) and groups every deduction line by type into
 * tabs: PAYE, Medical Aid, Bank Scheme, Loans, … plus Gratuity Tax.
 *
 * Each tab lists every staff member with that deduction, the amount,
 * and the remaining balance where relevant, with a running total.
 * A label filter isolates one provider/scheme (e.g. "Stanbic" under
 * Bank Scheme), and Export CSV drops the filtered list to a single
 * file — e.g. all Stanbic deductions for May 2026 — ready to send to
 * the provider or file with BURS (PAYE).
 *
 * Source = payslips, so generate the pay run for the month first.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { FileSpreadsheet, Download, AlertTriangle } from 'lucide-react';
import { GradientHero, SoftCard, StatChip, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useHrData } from '@/features/hr/useHrData';
import { periodLabel } from '@/lib/assemblePayslip';
import {
  fmtPula, round2, MONTH_LABELS,
  DEDUCTION_TYPE_ORDER, DEDUCTION_TYPE_LABELS, deductionCode,
} from '@/lib/hrPayroll';

interface Line {
  employeeNo: string;
  name: string;
  label: string;
  amount: number;
  remaining?: number;
}

function codeLabel(code: string): string {
  const type = code.toLowerCase();
  if (code === 'GRA_TAX') return 'Gratuity Tax';
  return (DEDUCTION_TYPE_LABELS as Record<string, string>)[type] ?? code;
}

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export default function DeductionReports({ embedded = false }: { embedded?: boolean }) {
  const data = useHrData();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [activeTab, setActiveTab] = useState<string>('PAYE');
  const [labelFilter, setLabelFilter] = useState<string>('all');

  // Deduction lines from this month's generated payslips, bucketed by code.
  const { buckets, tabs, slipCount } = useMemo(() => {
    const slips = data.payslips.filter((p) => p.period?.year === year && p.period?.month === month);
    const map = new Map<string, Line[]>();
    for (const p of slips) {
      for (const l of p.lines ?? []) {
        if (l.kind !== 'deduction') continue;
        const arr = map.get(l.code) ?? map.set(l.code, []).get(l.code)!;
        arr.push({
          employeeNo: p.employeeNo,
          name: p.staffName,
          label: l.label,
          amount: l.amount,
          ...(typeof l.remainingBalance === 'number' ? { remaining: l.remainingBalance } : {}),
        });
      }
    }
    // Tab order: PAYE first (the BURS remittance), then the catalogue
    // order, then Gratuity Tax, then anything unexpected.
    const ordered = ['PAYE', ...DEDUCTION_TYPE_ORDER.filter((t) => t !== 'paye').map(deductionCode), 'GRA_TAX'];
    const present = ordered.filter((c) => map.has(c));
    for (const c of map.keys()) if (!present.includes(c)) present.push(c);
    return { buckets: map, tabs: present, slipCount: slips.length };
  }, [data.payslips, year, month]);

  const active = buckets.has(activeTab) ? activeTab : (tabs[0] ?? '');
  const allLines = buckets.get(active) ?? [];
  const labels = useMemo(
    () => Array.from(new Set(allLines.map((l) => l.label))).sort(),
    [allLines],
  );
  const lines = labelFilter === 'all' ? allLines : allLines.filter((l) => l.label === labelFilter);
  const total = round2(lines.reduce((s, l) => s + l.amount, 0));
  const sorted = [...lines].sort((a, b) => a.name.localeCompare(b.name));

  function exportCsv() {
    const header = ['Employee No', 'Name', 'Item', 'Amount (BWP)', 'Remaining Balance'];
    const body = sorted.map((l) => [
      l.employeeNo, l.name, l.label, l.amount.toFixed(2), l.remaining != null ? l.remaining.toFixed(2) : '',
    ]);
    const footer = ['', '', `TOTAL (${codeLabel(active)})`, total.toFixed(2), ''];
    const csv = [header, ...body, footer].map((row) => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const tag = labelFilter === 'all' ? codeLabel(active) : labelFilter;
    a.download = `${tag.replace(/\s+/g, '_')}_${year}-${String(month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading payroll…" /></div>;
  }

  const period = { year, month, from: '', to: '' };

  return (
    <div className="space-y-6">
      {embedded ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-gray-500 dark:text-gray-400">{periodLabel(period)} · from generated payslips</p>
          <div className="flex items-center gap-2">
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm')}>
              {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm w-24')} />
          </div>
        </div>
      ) : (
        <GradientHero
          icon={FileSpreadsheet}
          title="Deduction Reports"
          subtitle={`${periodLabel(period)} · from generated payslips`}
          actions={
            <div className="flex items-center gap-2">
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="rounded-xl bg-white/15 text-white text-sm px-3 py-2 backdrop-blur-sm border border-white/25 [&>option]:text-gray-800">
                {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24 rounded-xl bg-white/15 text-white text-sm px-3 py-2 backdrop-blur-sm border border-white/25" />
            </div>
          }
        />
      )}

      {slipCount === 0 ? (
        <SoftCard hover={false}>
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
            <AlertTriangle size={16} />
            No payslips generated for {periodLabel(period)} yet. Run the Pay Run for this month, then the deductions will appear here.
          </div>
        </SoftCard>
      ) : tabs.length === 0 ? (
        <SoftCard hover={false}>
          <p className="text-sm text-gray-500 dark:text-gray-400">No deductions on this month's payslips.</p>
        </SoftCard>
      ) : (
        <>
          {/* Type tabs */}
          <div className="flex flex-wrap gap-2">
            {tabs.map((c) => (
              <button
                key={c}
                onClick={() => { setActiveTab(c); setLabelFilter('all'); }}
                className={`px-3 py-1.5 rounded-xl text-sm transition-colors ${
                  c === active
                    ? 'bg-[var(--accent-strong,#1b3a6b)] text-white font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {codeLabel(c)} <span className="opacity-60">({buckets.get(c)?.length ?? 0})</span>
              </button>
            ))}
          </div>

          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{codeLabel(active)}</h3>
                {labels.length > 1 && (
                  <select
                    value={labelFilter}
                    onChange={(e) => setLabelFilter(e.target.value)}
                    className={glassFieldClass('py-1.5 text-sm')}
                  >
                    <option value="all">All schemes ({labels.length})</option>
                    {labels.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                )}
              </div>
              <div className="flex items-center gap-3">
                <StatChip tone="emerald" value={`P${fmtPula(total)}`} label={`${sorted.length} staff`} />
                <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl">
                  <Download size={15} /> Export CSV
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 px-4 font-medium">Employee No</th>
                    <th className="py-2 px-4 font-medium">Name</th>
                    <th className="py-2 px-4 font-medium">Item</th>
                    <th className="py-2 px-4 font-medium text-right">Amount</th>
                    <th className="py-2 px-4 font-medium text-right">Remaining</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {sorted.map((l, i) => (
                    <tr key={`${l.employeeNo}_${i}`}>
                      <td className="py-2 px-4 text-gray-500 dark:text-gray-400">#{l.employeeNo}</td>
                      <td className="py-2 px-4 text-gray-800 dark:text-gray-100">{l.name}</td>
                      <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{l.label}</td>
                      <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-100">{fmtPula(l.amount)}</td>
                      <td className="py-2 px-4 text-right text-gray-500 dark:text-gray-400">{l.remaining != null ? fmtPula(l.remaining) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 dark:border-gray-600 font-semibold text-gray-800 dark:text-gray-100">
                    <td className="py-2.5 px-4" colSpan={3}>Total · {codeLabel(active)}{labelFilter !== 'all' ? ` · ${labelFilter}` : ''}</td>
                    <td className="py-2.5 px-4 text-right">{fmtPula(total)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </SoftCard>
        </>
      )}
    </div>
  );
}
